import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KILO } from '../../constants/KILO_THEME'
import { supabase } from '../../lib/supabaseClient'
import DeviceConnect from '../coach/DeviceConnect'
import ProUpgradeCard from '../coach/ProUpgradeCard'
import MaterialIcon from '../ui/MaterialIcon'
import { MATERIAL_ICONS } from '../../lib/materialIcons'
import { WEARABLE_CATALOG } from '../../lib/hardware/hardwareCatalog'
import { BleTelemetryController, isWebBluetoothAvailable } from '../../lib/hardware/bleManager'
import type { DiscoveredPeripheral, WearableCatalogEntry } from '../../lib/hardware/types'
import { useHardwareMetricLabels } from '../../hooks/useHardwareMetricLabels'

type PairingMatrixProps = {
  userId: string
  canWearables?: boolean
  onUpgrade?: () => void
  /** Receives the discovered peripheral AND its live (still-connected) controller. */
  onStartLiveSession?: (peripheral: DiscoveredPeripheral, controller: BleTelemetryController) => void
}

type PairingColumn = 'wearables' | 'equipment'
type ScanState = 'idle' | 'scanning' | 'found' | 'error'

const PairingMatrix = ({ userId, canWearables = false, onUpgrade, onStartLiveSession }: PairingMatrixProps) => {
  const { t } = useTranslation()
  const { labelFor } = useHardwareMetricLabels()
  const [activeColumn, setActiveColumn] = useState<PairingColumn>('equipment')
  const [wearableTypesExpanded, setWearableTypesExpanded] = useState(false)
  const [scanState, setScanState] = useState<ScanState>('idle')
  const [scanError, setScanError] = useState<string | null>(null)
  const [foundMachine, setFoundMachine] = useState<DiscoveredPeripheral | null>(null)

  const tierLabels: Record<number, string> = {
    1: t('workout.pairing.tier1'),
    2: t('workout.pairing.tier2'),
    3: t('workout.pairing.tier3'),
  }

  const wearablesByTier = useMemo(() => {
    const groups: Record<number, WearableCatalogEntry[]> = { 1: [], 2: [], 3: [] }
    for (const w of WEARABLE_CATALOG) {
      groups[w.tier].push(w)
    }
    return groups
  }, [])

  async function handleScanEquipment() {
    setScanError(null)
    setFoundMachine(null)
    if (!isWebBluetoothAvailable()) {
      setScanError(t('workout.pairing.bleError'))
      setScanState('error')
      return
    }

    setScanState('scanning')
    const controller = new BleTelemetryController()
    try {
      await controller.discover()
      const machine = await controller.connectAndStream()
      await registerHardware(machine)
      setFoundMachine(machine)
      setScanState('found')
      onStartLiveSession?.(machine, controller)
    } catch (err) {
      setScanError(err instanceof Error ? err.message : t('workout.pairing.noMachine'))
      setScanState('error')
    }
  }

  async function registerHardware(machine: DiscoveredPeripheral) {
    await supabase.from('user_hardware_connections').upsert(
      {
        user_id: userId,
        brand_slug: machine.name,
        device_model_string: machine.machineKind,
        connection_type: machine.connectionType,
        capabilities: machine.capabilities,
        is_active: true,
        last_sync_at: new Date().toISOString(),
      },
      { onConflict: 'user_id, brand_slug' },
    )
  }

  const pairingTabClass = (column: PairingColumn) => {
    const active = activeColumn === column
    return `group flex flex-1 flex-col items-start gap-1 rounded-xl px-3 py-2.5 text-left transition-colors ${
      active ? KILO.pill.active : `${KILO.text.muted} hover:bg-kilo-card`
    }`
  }

  const pairingTabHintClass = (column: PairingColumn) => {
    const active = activeColumn === column
    return `text-[10px] leading-snug ${active ? 'text-neutral-800' : KILO.text.soft}`
  }

  const pairingTabIconClass = (column: PairingColumn) => {
    const active = activeColumn === column
    return active ? '!text-black' : ''
  }

  return (
    <div className={`${KILO.bg.card} ${KILO.border.default} ${KILO.radius.card} overflow-hidden`}>
      <div className="border-b border-kilo-border px-4 py-3">
        <p className={KILO.meta}>{t('workout.pairing.peripheralMatrix')}</p>
      </div>

      <div className="border-b border-kilo-border px-4 py-3">
        <div
          className={`rounded-2xl border border-kilo-border bg-kilo-nested p-1`}
          role="tablist"
          aria-label={t('workout.pairing.peripheralMatrix')}
        >
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              role="tab"
              aria-selected={activeColumn === 'wearables'}
              onClick={() => setActiveColumn('wearables')}
              className={pairingTabClass('wearables')}
            >
              <span className="flex items-center gap-2">
                <MaterialIcon
                  name={MATERIAL_ICONS.watchScreentime}
                  filled={activeColumn === 'wearables'}
                  hoverable={activeColumn !== 'wearables'}
                  size="sm"
                  className={pairingTabIconClass('wearables')}
                />
                <span className="text-sm font-semibold">{t('workout.pairing.wearables')}</span>
              </span>
              <span className={pairingTabHintClass('wearables')}>{t('workout.pairing.wearablesHint')}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeColumn === 'equipment'}
              onClick={() => setActiveColumn('equipment')}
              className={pairingTabClass('equipment')}
            >
              <span className="flex items-center gap-2">
                <MaterialIcon
                  name="electric_bike"
                  filled={activeColumn === 'equipment'}
                  hoverable={activeColumn !== 'equipment'}
                  size="sm"
                  className={pairingTabIconClass('equipment')}
                />
                <span className="text-sm font-semibold">{t('workout.pairing.equipment')}</span>
              </span>
              <span className={pairingTabHintClass('equipment')}>{t('workout.pairing.equipmentHint')}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-5" role="tabpanel">
        {activeColumn === 'wearables' ? (
          <div className="space-y-5">
            <h3 className="text-lg font-semibold text-white">{t('workout.pairing.connectWearable')}</h3>

            {canWearables ? (
              <DeviceConnect variant="panel" />
            ) : onUpgrade ? (
              <ProUpgradeCard feature="wearables" onUpgrade={onUpgrade} />
            ) : null}

            <div className={`overflow-hidden rounded-2xl border border-kilo-border ${KILO.bg.card}`}>
              <button
                type="button"
                onClick={() => setWearableTypesExpanded((open) => !open)}
                className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-kilo-text/[0.02]"
                aria-expanded={wearableTypesExpanded}
              >
                <MaterialIcon name="info" hoverable size="sm" className="mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold ${KILO.text.primary}`}>
                    {t('workout.pairing.wearableTypesTitle')}
                  </p>
                  {!wearableTypesExpanded ? (
                    <p className={`mt-1 text-xs ${KILO.text.muted}`}>{t('workout.pairing.wearableTypesSummary')}</p>
                  ) : null}
                </div>
                <MaterialIcon
                  name={wearableTypesExpanded ? 'expand_less' : 'expand_more'}
                  size="sm"
                  className="shrink-0"
                />
              </button>

              {wearableTypesExpanded ? (
                <div className="space-y-4 border-t border-kilo-border px-4 pb-4 pt-3">
                  {[1, 2, 3].map((tier) =>
                    wearablesByTier[tier].length > 0 ? (
                      <div key={tier} className="space-y-2">
                        <p className={KILO.meta}>{tierLabels[tier]}</p>
                        <div className="flex flex-wrap gap-2">
                          {wearablesByTier[tier].map((w) => (
                            <span
                              key={w.brand_slug}
                              className="rounded-lg border border-neutral-800 bg-neutral-900 px-2.5 py-1.5 text-xs text-neutral-300"
                            >
                              {w.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null,
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-semibold text-white">{t('workout.pairing.pairEquipment')}</h3>
              <p className="mt-1 text-sm text-neutral-400">{t('workout.pairing.pairSubtitle')}</p>
            </div>

            <button
              type="button"
              onClick={() => void handleScanEquipment()}
              disabled={scanState === 'scanning'}
              className="w-full rounded-xl bg-kilo-accent py-3 font-semibold text-black transition-colors hover:bg-kilo-accent-dark disabled:opacity-50"
            >
              {scanState === 'scanning' ? t('workout.pairing.scanning') : t('workout.pairing.scanButton')}
            </button>

            {scanState === 'scanning' ? <ScanRadar /> : null}
            {scanError ? <p className="text-center text-xs text-red-400">{scanError}</p> : null}

            {foundMachine ? (
              <div className="rounded-2xl border border-kilo-accent/40 bg-kilo-accent/5 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-white">{foundMachine.name}</p>
                    <p className="text-[11px] uppercase tracking-wider text-kilo-accent">
                      {t('workout.pairing.machineFound', {
                        connection: foundMachine.connectionType.replace('LOCAL_', '').replace('_', ' '),
                        kind: foundMachine.machineKind,
                      })}
                    </p>
                  </div>
                  <span className="h-2.5 w-2.5 rounded-full bg-kilo-accent" />
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {foundMachine.capabilities.map((cap) => (
                    <span
                      key={cap}
                      className="rounded-md bg-neutral-900 px-2 py-1 text-[10px] font-medium text-neutral-300"
                    >
                      {labelFor(cap).label} {t('workout.pairing.streamingLive')}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

const ScanRadar = () => (
  <div className="flex items-center justify-center gap-1.5 py-2" aria-label="Scanning">
    {[0, 1, 2, 3, 4].map((i) => (
      <span
        key={i}
        className="h-6 w-1 animate-pulse rounded-full bg-kilo-accent/70"
        style={{ animationDelay: `${i * 120}ms`, animationDuration: '900ms' }}
      />
    ))}
  </div>
)

export default PairingMatrix
